<template>
  <fullscreen-dialog
    v-model="model"
    :title="$t('join_restoran')"
    :subtitle="firm?.name ? firm.name + ' | ID: ' + firm.id : ''"
    icon="mdi-cog-outline"
    :loading="saving"
    :disabled="saving"
    @close="close"
    @save="save"
  >
    <template #filters>
      <v-row class="px-3 py-2" align="center">
        <v-col cols="12" md="4">
          <autocomplete
            @change="onCountryChange"
            v-model="editedItem.country"
            :items="countries"
            :label="$t('country')"
            required
            item-text="name"
            item-id="id"
            return-object
          />
        </v-col>
        <v-col cols="12" md="4">
          <autocomplete
            v-model="editedItem.city"
            @change="onCityChange"
            :items="cities ? cities : []"
            :label="$t('city')"
            required
            item-text="name"
            item-id="id"
            return-object
          />
        </v-col>
        <v-col cols="12" md="4">
          <text-field
            v-model="searchRes"
            append-icon="mdi-magnify"
            :label="$t('search')"
            hide-details
          />
        </v-col>
      </v-row>
    </template>

    <div class="pb-4">
      <div v-if="restaurants.length" class="summary-row d-flex align-center mb-3">
        <v-icon color="primary" small class="mr-2">mdi-store-outline</v-icon>
        <span class="font-weight-bold">{{ $t("restaurants") }}</span>
        <v-chip small color="grey lighten-4" class="ml-3 font-weight-medium">
          {{ restaurants.length }}
        </v-chip>
        <v-chip small class="ml-2 font-weight-medium linked-chip">
          <span class="status-dot mr-1"></span>
          {{ linkedCount }} {{ $t("linked") }}
        </v-chip>
      </div>

      <v-row justify="center" class="py-10" no-gutters v-if="loading">
        <v-col cols="auto">
          <v-progress-circular indeterminate color="primary" />
        </v-col>
      </v-row>

      <empty-state
        v-else-if="!restaurants.length"
        icon="mdi-store-search-outline"
        :title="$t('no_restaurants_found')"
        :subtitle="$t('select_city_to_see_restaurants')"
      />

      <v-data-table
        v-else
        :headers="headers"
        :items="filteredRestaurants"
        class="ordera-box-shadow settings-table"
        item-key="id"
        hide-default-footer
        disable-pagination
        mobile-breakpoint="0"
        :item-class="(item) => (isLinked(item) ? 'row-linked' : '')"
      >
        <template v-slot:item.check="{ item }">
          <v-checkbox
            :input-value="isLinked(item)"
            hide-details
            dense
            class="mt-0 pt-0 d-inline-flex justify-center"
            color="primary"
            @change="onSelectRestaurant(item, $event)"
          />
        </template>
        <template v-slot:item.id="{ item }">
          <span class="restaurant-id">{{ item.id }}</span>
        </template>
        <template v-slot:item.name="{ item }">
          <span class="font-weight-medium">{{ toLatin(item.name) }}</span>
        </template>
        <template v-slot:item.active_restoran="{ item }">
          <v-checkbox
            v-model="item.active_restoran"
            hide-details
            dense
            class="mt-0 pt-0 d-inline-flex justify-center"
            color="primary"
            @change="markLinked(item)"
          />
        </template>
        <template v-slot:item.active_company="{ item }">
          <v-checkbox
            v-model="item.active_company"
            hide-details
            dense
            class="mt-0 pt-0 d-inline-flex justify-center"
            color="primary"
            @change="markLinked(item)"
          />
        </template>
        <template v-slot:item.paying_type="{ item }">
          <autocomplete
            v-model="item.paying_type"
            :items="payingTypeOptions"
            item-text="name"
            item-value="val"
            hide-details
            dense
            @change="markLinked(item)"
          />
        </template>
        <template v-slot:item.paying="{ item }">
          <text-field
            v-model="item.paying"
            hide-details
            dense
            @change="markLinked(item)"
          />
        </template>
        <template v-slot:item.internal="{ item }">
          <v-checkbox
            v-model="item.internal"
            hide-details
            dense
            class="mt-0 pt-0 d-inline-flex justify-center"
            color="primary"
            @change="markLinked(item)"
          />
        </template>
      </v-data-table>
    </div>

    <global-confirm-box ref="confirmBox" />
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantService from "@/services/restaurant";
import companyService from "@/services/company";
import {
  hasCurrencyMismatch,
  getRestaurantCurrency,
  getDeliveryCompanyCurrency,
} from "@/utils/currency";

export default {
  name: "RestaurantSettingsDialog",
  components: {
    FullscreenDialog,
    EmptyState,
  },
  mixins: [hasModel, hasSnackbar],
  props: {
    firm: {
      type: Object,
      default: () => ({}),
    },
    countries: {
      type: Array,
      default: () => [],
    },
    cities: {
      type: Array,
      default: () => [],
    },
    editedItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    searchRes: "",
    loading: false,
    saving: false,
    // Restorani već povezani sa ovom firmom - ostaju vidljivi bez obzira na izabrani grad.
    linkedRestaurants: [],
    // Svi restorani u trenutno izabranom gradu (kandidati za povezivanje).
    cityRestaurants: [],
  }),
  computed: {
    headers() {
      return [
        { text: this.$t("status"), value: "check", sortable: false, width: "64px" },
        { text: "ID", value: "id", align: "end", width: "64px" },
        { text: this.$t("name"), value: "name" },
        {
          text: this.$t("status_restoran"),
          value: "active_restoran",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("status_company"),
          value: "active_company",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("pay_type"),
          value: "paying_type",
          sortable: false,
          width: "200px",
        },
        { text: this.$t("amount"), value: "paying", sortable: false, width: "140px" },
        {
          text: this.$t("internal"),
          value: "internal",
          sortable: false,
          align: "center",
        },
      ];
    },
    payingTypeOptions() {
      return [
        { name: this.$t("choose"), val: 0 },
        { name: this.$t("by_month"), val: 1 },
        { name: this.$t("by_percent"), val: 2 },
        { name: this.$t("by_meal"), val: 3 },
      ];
    },
    // Povezani restorani uvek na vrhu:
    //  1. oni koje firma već nosi sa sobom (linkedRestaurants) - vidljivi i pre izbora grada,
    //  2. restorani iz izabranog grada koji su povezani sa OVOM firmom,
    //  3. ostali restorani iz grada.
    // Restorani se ne dupliraju izmedju grupa.
    restaurants() {
      const linkedIds = new Set(this.linkedRestaurants.map((item) => item.id));
      const fromCity = this.cityRestaurants.filter((item) => !linkedIds.has(item.id));
      const linkedInCity = fromCity.filter((item) => this.isLinked(item));
      const unlinkedInCity = fromCity.filter((item) => !this.isLinked(item));
      return [...this.linkedRestaurants, ...linkedInCity, ...unlinkedInCity];
    },
    filteredRestaurants() {
      if (!this.searchRes || !this.searchRes.trim()) return this.restaurants;
      const search = this.toLatin(this.searchRes).trim().toLowerCase();
      return this.restaurants.filter((item) =>
        this.toLatin(item.name || "")
          .toLowerCase()
          .includes(search)
      );
    },
    linkedCount() {
      return this.restaurants.filter((item) => this.isLinked(item)).length;
    },
  },
  watch: {
    model: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.loadLinkedRestaurants();
          if (this.editedItem.city) {
            this.loadRestaurantsByCity();
          }
        }
      },
    },
  },
  methods: {
    isLinked(item) {
      return !!item.restaurant_id;
    },
    // Bilo koja izmena podešavanja (način plaćanja, iznos, status...) znači da
    // korisnik želi da poveže restoran, pa ne treba da mu promakne da ništa
    // neće biti sačuvano samo zato što nije prvo čekirao "status" kutijicu.
    async markLinked(item) {
      if (item.restaurant_id) return;
      if (!(await this.confirmCurrencyMismatch(item))) return;
      item.restaurant_id = item.id;
    },
    async onSelectRestaurant(item, checked) {
      if (checked && !(await this.confirmCurrencyMismatch(item))) {
        // Korisnik je odustao - vrati kutijicu u nečekirano stanje.
        this.$forceUpdate();
        return;
      }
      item.restaurant_id = checked ? item.id : null;
      this.$emit("restaurant-selected", item);
    },
    // Vrati true ako je saradnju u redu uključiti (valuta usklađena ili korisnik potvrdio).
    async confirmCurrencyMismatch(item) {
      if (!hasCurrencyMismatch(item, this.firm)) return true;
      try {
        await this.$refs.confirmBox.open(
          this.$t("currency_mismatch_title"),
          this.$t("currency_mismatch_warning", {
            restaurant: this.toLatin(item.name || ""),
            restaurantCurrency: getRestaurantCurrency(item),
            companyCurrency: getDeliveryCompanyCurrency(this.firm),
          }),
          { color: "warning", width: 460 }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    // Restoran je "povezan" sa OVOM firmom samo ako postoji zapis čiji je
    // delivery_company_id jednak id-u trenutne firme (isti restoran može biti
    // povezan sa više firmi za dostavu).
    findLinkForFirm(item) {
      if (!item.restoran_delivery_company || !item.restoran_delivery_company.length) {
        return null;
      }
      return (
        item.restoran_delivery_company.find(
          (link) => link.delivery_company_id == this.firm.id
        ) || null
      );
    },
    normalizeCityRestaurant(item) {
      const match = this.findLinkForFirm(item);
      return {
        ...item,
        restaurant_id: match ? match.restaurant_id : null,
        active_restoran: match ? !!match.active_restoran : false,
        active_company: match ? !!match.active_company : false,
        paying_type: match ? match.paying_type : null,
        paying: match ? match.paying : null,
        internal: match ? !!match.internal : false,
      };
    },
    // Firma već nosi svoje povezane restorane u firm.restaurant_delivery_company
    // (svaki zapis ima ugnježden .restaurant + polja veze) - nema potrebe za
    // dodatnim pozivom API-ja, i prikazuju se odmah bez čekanja na grad.
    loadLinkedRestaurants() {
      const links = (this.firm && this.firm.restaurant_delivery_company) || [];
      this.linkedRestaurants = links
        .filter((link) => link.restaurant)
        .map((link) => ({
          ...link.restaurant,
          restaurant_id: link.restaurant_id,
          active_restoran: !!link.active_restoran,
          active_company: !!link.active_company,
          paying_type: link.paying_type,
          paying: link.paying,
          internal: !!link.internal,
        }));
    },

    close() {
      this.$emit("close");
    },

    async save() {
      this.saving = true;
      try {
        let data = {};
        // Backend ignoriše redove sa restaurant_id == null (nema unlink-a), pa
        // šaljemo samo povezane restorane. Vidi 07_09_2026_Backend_odgovori_dostavne_firme.
        data["restorani"] = this.restaurants.filter((item) => item.restaurant_id != null);
        data["delivery_company"] = this.firm;
        data["restoran_id"] = this.$store.state.restaurant.id;
        await companyService.storeDeliveryCompanyRestaurants(data);
        this.showSuccessSnackbar(this.$t("firm_settings_saved"));
        this.$emit("saved");
        this.close();
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_saving_firm_settings"));
      } finally {
        this.saving = false;
      }
    },

    onCountryChange() {
      this.$emit("country-change");
    },

    onCityChange() {
      this.loadRestaurantsByCity();
    },

    async loadRestaurantsByCity() {
      this.loading = true;
      try {
        const { data } = await restaurantService.getRestaurantsByCity(
          this.editedItem.city
        );
        this.cityRestaurants = (data || []).map((item) =>
          this.normalizeCityRestaurant(item)
        );
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_restaurants"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.summary-row {
  padding: 4px 2px;
}

.linked-chip {
  background: #e0f5e6 !important;
  color: #157f3c !important;
}

.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #157f3c;
}

.settings-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.settings-table >>> tbody tr.row-linked {
  background-color: #f1faf3 !important;
}
</style>
